
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomerNumberStep from './steps/CustomerNumberStep';
import CustomerConnectStep from './steps/CustomerConnectStep';
import VehicleIdentificationStep from './steps/VehicleIdentificationStep';
import VehicleDetailsStep from './steps/VehicleDetailsStep';
import DeviceStep from './steps/DeviceStep';
import ReviewStep from './steps/ReviewStep';
import { store } from '@/service/store';
import { useSignalEffect } from '@preact/signals-react';
import AssistantHeader from './components/AssistantHeader';
import AssistantStepper from './components/AssistantStepper';
import AssistantFooter from './components/AssistantFooter';
import apiService from '@/service/api-service';
import { useToast } from '@/hooks/use-toast';
import {licenseNumberPattern, vinPattern} from "@/utils/patterns.ts";

interface InstallationAssistantProps {
  open: boolean;
  onClose: () => void;
}

const InstallationAssistant: React.FC<InstallationAssistantProps> = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [deviceData, setDeviceData] = useState({
    taximeterVendor: '',
    taximeterType: '',
    taximeterSerial: '',
    taximeterYearOfProduction: '',
    deviceVendor: '',
    deviceType: '',
    deviceSerial: '',
    deviceYearOfProduction: ''
  });
  const [isVehicleDetailsValid, setIsVehicleDetailsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation();
  const { toast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [customerNumber, setCustomerNumber] = useState<string>('');
  const [customerConnected, setCustomerConnected] = useState(false);
  const [vehicleVIN, setVehicleVIN] = useState<string>('');
  const [vehicleMileage, setVehicleMileage] = useState<string>('');
  const [licenseNumber, setLicenseNumber] = useState<string>('');
  const [carNotRegistered, setCarNotRegistered] = useState(false);
  const [businessName, setBusinessName] = useState<string>('');

  useEffect(() => {
    if (customerNumber) {
      // Fetch customer details to get the business name
      const customer = store.customers.get(customerNumber);
      if (customer && customer.profile?.data?.value) {
        setBusinessName(customer.profile.data.value.businessName || '');
      } else {
        // If customer profile isn't loaded yet, load it
        if (customer) {
          customer.profile.load().then(() => {
            if (customer.profile.data.value) {
              setBusinessName(customer.profile.data.value.businessName || '');
            }
          });
        }
      }
    }
  }, [customerNumber]);

  useSignalEffect(() => {
    setCustomerNumber(store.installationAssistant.customerNumber.value || '');
    setCustomerConnected(store.installationAssistant.customerIsKnown.value);
    setVehicleVIN(store.installationAssistant.vehicleVIN.value || '');
    setLicenseNumber(store.installationAssistant.licenseNumber.value || '');
    setVehicleMileage(store.installationAssistant.vehicleMileage.value || '');
    setCarNotRegistered(store.installationAssistant.carNotRegistered.value);
  });

  const handleSubmitInstallation = async () => {
    if (activeStep === 5) {
      setIsSubmitting(true);
      try {
        await apiService.createInstallation(customerNumber, {
          taximeterVendor: deviceData.taximeterVendor,
          taximeterType: deviceData.taximeterType,
          taximeterSerial: parseInt(deviceData.taximeterSerial),
          taximeterYearOfProduction: parseInt(deviceData.taximeterYearOfProduction),
          deviceVendor: deviceData.deviceVendor || undefined,
          deviceType: deviceData.deviceType || undefined,
          deviceSerial: deviceData.deviceSerial ? parseInt(deviceData.deviceSerial) : undefined,
          deviceYearOfProduction: deviceData.deviceYearOfProduction ? parseInt(deviceData.deviceYearOfProduction) : undefined,
          vin: vehicleVIN,
          licenseNumber: licenseNumber === '' ? null : licenseNumber,
          mileage: vehicleMileage === '' ? 0 : parseInt(vehicleMileage)
        });

        toast({
          title: t('assistant.installation.success'),
          description: t('assistant.installation.successDescription'),
        });

        handleClose();
      } catch (error) {
        console.error('Installation creation failed:', error);
        toast({
          variant: "destructive",
          title: t('assistant.installation.error'),
          description: t('assistant.installation.errorDescription'),
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleNext = async () => {
    if (activeStep === 5) {
      await handleSubmitInstallation();
      return;
    } else if (activeStep === 0) {
      if (customerConnected) {
        setActiveStep(2);
      } else {
        setActiveStep(1);
      }
    } else if (activeStep === 2) {
      if (store.installationAssistant.vehicleExists.value) {
        setActiveStep(4); // Skip vehicle details step
      } else {
        setActiveStep(3); // Go to vehicle details for new vehicles
      }
    } else if (activeStep === 3) {
      // For vehicle details step, trigger the form submission
      // @ts-ignore - Custom property added to window
      if (window.submitVehicleDetailsForm && window.submitVehicleDetailsForm()) {
        return;
      }
      // If form submission function doesn't exist or fails, don't proceed
      console.warn("Could not trigger vehicle details form submission");
      return;
    } else if (activeStep === 4) {
      // For device step, trigger the form submission via the DeviceStep component's reference
      // We need to pass in a function that will be called by the DeviceStep
      return;
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 2 && customerConnected) {
      setActiveStep(0);
    } else if (activeStep === 4 && store.installationAssistant.vehicleExists.value) {
      setActiveStep(2);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    store.installationAssistant.reset();
    setCustomerNumber('');
    setCustomerConnected(false);
    setVehicleVIN('');
    setVehicleMileage('');
    store.installationAssistant.vehicleExists.value = false;
    setLicenseNumber('');
    setBusinessName('');
    setDeviceData({
      taximeterVendor: '',
      taximeterType: '',
      taximeterSerial: '',
      taximeterYearOfProduction: '',
      deviceVendor: '',
      deviceType: '',
      deviceSerial: '',
      deviceYearOfProduction: ''
    });
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const isNextButtonDisabled = () => {
    if (activeStep === 0) {
      return !customerNumber;
    }
    if (activeStep === 1) {
      return !customerConnected;
    }
    if (activeStep === 2) {
      const customer = store.customers.get(customerNumber)
      const cars = customer.cars.data.value || [];
      const selectedCar = cars.find(car => car.vin === store.installationAssistant.vehicleVIN.value);
      const hasValidVIN = store.installationAssistant.vehicleVIN.value &&
                         store.installationAssistant.vehicleVIN.value.length === 17 &&
                         vinPattern.test(store.installationAssistant.vehicleVIN.value)
      const hasValidLicenseNumber =  store.installationAssistant.carNotRegistered.value || (
        store.installationAssistant.licenseNumber.value &&
        licenseNumberPattern.test(store.installationAssistant.licenseNumber.value)
      );

      if (selectedCar && selectedCar.currentInstallation !== null) {
        return true;
      }

      return !(
        (selectedCar || hasValidVIN) &&
        hasValidLicenseNumber
      );
    }
    if (activeStep === 3) {
      return !isVehicleDetailsValid;
    }
    if (activeStep === 4) {
      // Always need taximeter info
      if (!deviceData.taximeterVendor || !deviceData.taximeterType ||
          !deviceData.taximeterSerial || !deviceData.taximeterYearOfProduction) {
        return true;
      }

      // Taximeter serial must be at least 4 digits
      if (deviceData.taximeterSerial.length < 4) {
        return true;
      }

      // Check if signing device is needed
      const needsSigningDevice = () => {
        if (deviceData.taximeterVendor === 'semitron') return false;
        if (deviceData.taximeterVendor === 'hale' &&
            (deviceData.taximeterType === 'MCT07' || deviceData.taximeterType === 'WSZ07')) {
          return false;
        }
        if (deviceData.taximeterVendor === 'digitax' && deviceData.taximeterType) return true;
        if (deviceData.taximeterVendor === 'hale' && deviceData.taximeterType) return true;
        return false;
      };

      // If signing device is needed, validate those fields
      if (needsSigningDevice()) {
        // For Digitax (TBOX), deviceYearOfProduction is auto-filled
        if (deviceData.taximeterVendor === 'digitax') {
          if (!deviceData.deviceVendor || !deviceData.deviceType || !deviceData.deviceSerial) {
            return true;
          }
          // TBOX serial must be exactly 10 digits
          if (deviceData.deviceType === 'TBOX' && deviceData.deviceSerial.length !== 10) {
            return true;
          }
          return false;
        }
        return !deviceData.deviceVendor || !deviceData.deviceType ||
               !deviceData.deviceSerial || !deviceData.deviceYearOfProduction;
      }

      return false;
    }
    return false;
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <CustomerNumberStep/>;
      case 1:
        return <CustomerConnectStep
          customerNumber={customerNumber}
          setCustomerConnected={setCustomerConnected}
        />;
      case 2:
        return <VehicleIdentificationStep/>;
      case 3:
        return <VehicleDetailsStep
          setIsValid={setIsVehicleDetailsValid}
          onNextStep={() => setActiveStep((prev) => prev + 1)}
        />;
      case 4:
        return <DeviceStep
          deviceData={deviceData}
          setDeviceData={setDeviceData}
          onNextStep={() => setActiveStep((prev) => prev + 1)}
        />;
      case 5:
        return <ReviewStep
          customerNumber={customerNumber}
          businessName={businessName}
          vehicleVIN={vehicleVIN}
          vehicleMileage={vehicleMileage}
          licenseNumber={licenseNumber}
          deviceData={deviceData}
        />;
      default:
        return "Unknown step";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown') {
          handleClose();
        }
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100dvh' : '80vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: isMobile ? 0 : 2,
          bgcolor: 'background.default',
          backgroundImage: 'none',
          m: isMobile ? 0 : undefined
        }
      }}
    >
      <AssistantHeader onClose={handleClose} />
      <AssistantStepper
        activeStep={activeStep}
        customerConnected={customerConnected}
        vehicleExists={store.installationAssistant.vehicleExists.value}
      />
      <DialogContent sx={{
        p: 3,
        flexGrow: 1,
        overflow: 'auto',
        height: isMobile ? 'calc(100dvh - 128px)' : 'auto' // Adjust for header and footer height
      }}>
        {getStepContent(activeStep)}
      </DialogContent>
      <AssistantFooter
        activeStep={activeStep}
        totalSteps={6}
        onBack={handleBack}
        onNext={handleNext}
        isNextDisabled={isNextButtonDisabled()}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
};

export default InstallationAssistant;
